/**
 * Tipos para el módulo CVU (Pronto Pago) — Fase 6.1
 *
 * Documentos relacionados:
 *   - requerimientos.md § 6.1 (Integración Pronto Pago)
 *   - DECISIONES.md #007 (REST + bearer token), #026 (adapter mock)
 *
 * El backend (`api/`) decide internamente entre el servicio real y el mock
 * según `isProntoPagoConfigured()`. La UI recibe `mode` en cada respuesta
 * para mostrar el banner "MODO MOCK" cuando corresponda.
 */

export type CvuEstado = 'activa' | 'inactiva' | 'pendiente';
export type ProntoPagoMode = 'mock' | 'real';

export interface Cvu {
  id: string;
  empresa_id: string;
  /** 22 dígitos */
  cvu: string;
  /** Máx. 20 chars (resolución BCRA) — formato `{prefix}.{cuit}` */
  alias: string;
  /** CUIT sin guiones, máx. 12 chars */
  name: string;
  /** Formato `{service}-{name}` */
  external_id: string | null;
  /** ID interno de Pronto Pago */
  pronto_pago_id: string | null;
  branch_id: string | null;
  service_code: string | null;
  enabled: boolean;
  estado: CvuEstado;
  created_at: string;
  updated_at: string;
  empresa?: {
    id: string;
    cuit: string;
    razon_social: string;
  };
}

export interface CvuConfig {
  mode: ProntoPagoMode;
  env: 'dev' | 'prod';
  alias_prefix: string;
  service_code: string;
  api_url: string;
}

export interface CvuSyncReport {
  total_remoto: number;
  total_local: number;
  /** CVUs que existen en Pronto Pago pero no en BD local. */
  en_remoto_sin_local: Array<{
    id: string;
    name: string;
    cvu: string;
    alias: string;
    externalId: string;
    enabled: boolean;
  }>;
  /** CVUs que existen en BD local pero no en Pronto Pago (huérfanos). */
  en_local_sin_remoto: Array<{
    cvu: string;
    name: string;
    pronto_pago_id: string | null;
  }>;
  /** Alias que se completaron desde Pronto Pago en este sync (#492). */
  alias_actualizados?: number;
}

export interface CvuLoteResult {
  id_lote: string;
  generadas: number;
  omitidas: number;
  cvus: Cvu[];
  /** Empresas/CVUs a las que el proveedor no asignó alias (#492). */
  sin_alias?: string[];
}

// ── Helpers ───────────────────────────────────────────────────────────────────

export function getEstadoCvuLabel(estado: CvuEstado | string): string {
  const labels: Record<string, string> = {
    activa:    'Activa',
    inactiva:  'Inactiva',
    pendiente: 'Pendiente',
  };
  return labels[estado] ?? estado;
}

export function getEstadoCvuColor(
  estado: CvuEstado | string,
): 'success' | 'default' | 'warning' {
  switch (estado) {
    case 'activa':    return 'success';
    case 'inactiva':  return 'default';
    case 'pendiente': return 'warning';
    default:          return 'default';
  }
}

/** Formato CVU en bloques de 4 para lectura: `2850 5900 ... 0123` */
export function formatCvu(cvu: string | null | undefined): string {
  if (!cvu) return '-';
  return cvu.replace(/(.{4})/g, '$1 ').trim();
}
